import React, { useLayoutEffect, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { gsap } from 'gsap';

// ** CRITICAL **
// We must register the components Chart.js needs
Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Helper to get HEX colors for Chart.js
const HexColor =  {
  'Red Bull': '#06002D',
  'Ferrari': '#D90516',
  'Mercedes': '#00A19B',
  'McLaren': '#FF8700',
  'Aston Martin': '#00594F',
  'Alpine': '#0090FF',
  'Williams': '#005AFF',
  'RB F1 Team': '#003E60', // Visa Cash App RB
  'Sauber': '#00E676', // Stake F1 Team Kick Sauber
  'Haas F1 Team': '#E6002B',
  'AlphaTauri': '#0A2340',      // Pre-RB (Navy Blue)
  'Alfa Romeo': '#960000',      // Pre-Sauber (Dark Red)
  'Renault': '#FFD800',        // Pre-Alpine (Yellow)
  'Racing Point': '#F596C8',    // Pre-Aston Martin (Pink)
  'Force India': '#F596C8',    // Pre-Racing Point (Pink)
  'Toro Rosso': '#002DFF', 
  'default': '#888888',
  };
  const getTeamHexColor = (constructorName) => {
    return HexColor[constructorName] || HexColor['default'];
  }

// --- Podium Card Component ---
const PodiumCard = React.forwardRef(({ driver, position, probability, delay }) => {
  const cardRef = useRef(null);
  const orderStyle = {
    1: "h-full", // P1 in middle, tallest
    2: "h-5/6", // P2 on left
    3: "h-4/6", // P3 on right
  }
  const color = getTeamHexColor(driver.constructor_name);

  useEffect(() => {
    // Animate card with GSAP
    gsap.fromTo(cardRef.current, 
      { opacity: 0, y: 50 }, 
      { opacity: 1, y: 0, duration: 0.5, delay: delay }
    );
  }, [delay]);

  return (
    <div
      ref={cardRef}
      style={{backgroundColor: color}}
      className={`w-1/3 flex flex-col items-center justify-end shadow-lg border border-f1-gray-light px-2 ${orderStyle[position]}`}
    >
      <div className={`p-4 rounded-t-lg shadow-lg w-full`}>
        <div className={`flex items-center justify-center w-12 h-12 mx-auto rounded-full`}>
          <span className="text-4xl font-bold">{position}</span>
        </div>
        <div className="text-center mt-3">
          <p className="text-lg md:text-xl font-bold text-white truncate">{driver.driver_name}</p>
          <p className="text-sm text-white/50 truncate">{driver.constructor_name}</p>
          <p className="text-2xl font-bold text-white mt-2">
            {(probability * 100).toFixed(1)}%
          </p>
          <p className="text-xs text-white/50">Podium Chance</p>
        </div>
      </div>
    </div>
  );
});

// --- Main Results Page Component ---
function ResultsPage({ predictions, onStartOver, raceInfo }) {
  const podium = predictions.slice(0, 3);
  const podiumRef = useRef(null);
  const chartRef = useRef(null);

  // --- Animation ---
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the podium cards
      gsap.from(".podium-card", {
        opacity: 0,
        y: 100,
        duration: 0.8,
        stagger: 0.2, // Animate one after another
        ease: "power3.out",
      });
      
      // Animate the chart
      gsap.from(chartRef.current, {
        opacity: 0,
        y: 50,
        duration: 1,
        delay: 0.5, // Start after podium
      });
    }, podiumRef); // Scope to the podium section
    
    return () => ctx.revert();
  }, []);
  
  // --- Chart.js Data & Options ---
  const chartData = {
    labels: predictions.map(p => p.driver_name),
    datasets: [
      {
        label: 'Podium Probability',
        data: predictions.map(p => p.podium_probability * 100),
        backgroundColor: predictions.map(p => getTeamHexColor(p.constructor_name)),
        borderColor: predictions.map(p => getTeamHexColor(p.constructor_name)),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    indexAxis: 'y', // This makes it a horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // No legend, the labels are clear
      },
      title: {
        display: true,
        text: 'Full Prediction Breakdown',
        color: 'f1-black',
        font: { size: 18 },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return ` ${context.dataset.label}: ${context.raw.toFixed(2)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Podium Probability (%)',
          color: 'f1-gray-dark'
        },
        ticks: { color: 'f1-gray-dark' }
      },
      y: {
        ticks: { color: 'f1-gray-dark', font: { size: 10 } },
        grid: { display: false }
      }
    }
  };

  return (
    <div className="space-y-12">
      {/* --- Header --- */}
      <div className="flex justify-between items-center">
        <button 
          onClick={onStartOver} 
          className="text-f1-black hover:text-f1-red transition-colors"
        >
          &larr; Start New Prediction
        </button>
        <h2 className="text-xl md:text-2xl font-semibold text-f1-gray-dark text-right">
          Results: {raceInfo.name} <span className="text-f1-gray-dark">({raceInfo.year})</span>
        </h2>
      </div>

      {/* --- Section 1: The Podium --- */}
      <div 
        ref={podiumRef} 
        className="h-80 md:h-96 flex items-end justify-center overflow-hidden"
      >
        {/* P2 - 2nd Place */}
        <PodiumCard 
          driver={podium[1]} 
          position={2} 
          probability={podium[1].podium_probability}
          delay={0.2}
        />
        {/* P1 - 1st Place */}
        <PodiumCard 
          driver={podium[0]} 
          position={1} 
          probability={podium[0].podium_probability}
          delay={0}
        />
        {/* P3 - 3rd Place */}
        <PodiumCard 
          driver={podium[2]} 
          position={3} 
          probability={podium[2].podium_probability}
          delay={0.4}
        />
      </div>

      {/* --- Section 2: Full Data Chart --- */}
      <div ref={chartRef} className="p-4 bg-white border border-f1-gray-light rounded-lg shadow-lg">
        <div style={{ height: '600px' }}> {/* Container to give Chart.js a height */}
          <Bar options={chartOptions} data={chartData} />
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;